import { CartEvent } from "@/global/store/data/purchaseSeats/cartManager";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import {
  calculateSuiteName,
  calculateTicketPlusPerksPrice,
  createSeatLevelCardTitle,
} from "../../ChooseSeats/utils.chooseSeats";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { v4 as uuid } from "uuid";
import {
  GroupLevelInventory,
  InventoryType,
  SeatLevelMultiInventory,
  SuitLevelInventory,
  SuitLevelV2Inventory,
} from "@/global/store/data/types";
import { useAppStore } from "@/global/store";
import { selectEventsSelectedById } from "@/global/store/data/purchaseSeats/events";
import { inventoryTypeUIDs } from "@/global/config/constants";
import { createCombinedSeats } from "../../utils.purchaseSeats";

// util
// prettier-ignore
function normalizeLocation(inventoryType: string, location?: string, displaySectionName?: string, isRetry: boolean = false) {
  let result = "";
  if (inventoryType == "SECTION-LEVEL" && location) {
    let [_, section, row, firstSeat, lastSeat] = location.split("_");
    result = `Section ${isRetry? section: displaySectionName || section}, Row ${row}, Seat ${firstSeat}-${lastSeat}`;
    if (firstSeat == lastSeat) {
      result = `Section ${section}, Row ${row}, Seat ${firstSeat}`;
    }
  }
  return result;
}

/*
+------------------------------------------------------------------------------+
* [ Checkout Inventory Card ]
+------------------------------------------------------------------------------+ */
interface CheckoutInventoryCard {
  inventory: CartEvent["products"][0];
  cartEvent?: CartEvent | null;
  isRetry?: boolean;
}

export function CheckoutInventoryCard(props: CheckoutInventoryCard) {
  const product = props.inventory as any;
  const cartEvent = props.cartEvent;
  const isRetry = props.isRetry;
  const teamConfig = useTeamConfig();

  return (
    <>
      {product.inventoryType === inventoryTypeUIDs.seatLevel ? (
        <InventoryCard
          key={product.id}
          leftTitle={createSeatLevelCardTitle(product, teamConfig)}
          rightTitle={getUSCurrency(Number(product.allInPrice) * product.seatCount)} // prettier-ignore
          price={product.allInPrice}
          seatCount={product.seatCount}
          allInPrice={product.allInPrice} // properly handle this accross the entire app to use price instead
        />
      ) : product.inventoryType === inventoryTypeUIDs.seatLevelMulti ? (
        <SeatLevelMultiInventoryCard key={product.id} inventory={product} />
      ) : product.inventoryType === inventoryTypeUIDs.sectionLevel ? (
        <InventoryCard
          key={product.id}
          leftTitle={normalizeLocation(inventoryTypeUIDs.sectionLevel, cartEvent?.location, product.displaySectionName, isRetry)} // prettier-ignore
          rightTitle={getUSCurrency(Number(product.allInPrice) * product.seatCount)} // prettier-ignore
          price={product.allInPrice}
          seatCount={product.seatCount}
          allInPrice={product.allInPrice}
        />
      ) : product.inventoryType === inventoryTypeUIDs.ada ? (
        <InventoryCard
          key={product.id}
          leftTitle={`Section ${product.sectionName}, Row ${product.rowName}, Seat ${product?.seatNumber}`}
          rightTitle={""}
          price={product.allInPrice}
          seatCount={product.seatCount}
          allInPrice={product.allInPrice}
        />
      ) : product.inventoryType == inventoryTypeUIDs.suitLevel ? (
        <SuiteLevelInventoryCard key={product.id} inventory={product} />
      ) : product.inventoryType === inventoryTypeUIDs.suiteLevelV2 ? (
        <SuiteLevelV2InventoryCard key={product.id} inventory={product} />
      ) : product.inventoryType === inventoryTypeUIDs.groupLevel ? (
        <GroupLevelInventoryCard key={product.id} inventory={product} />
      ) : null}
    </>
  );
}

/*
+------------------------------------------------------------------------------+
+
+
+
+
+ 
+
* [ General Inventory Card]
+------------------------------------------------------------------------------+ */
interface InventoryCardProps {
  leftTitle: string;
  rightTitle: string;
  price: string;
  seatCount: number;
  allInPrice?: number;
}

const InventoryCard = ({
  leftTitle,
  rightTitle,
  price,
  seatCount,
  allInPrice,
}: InventoryCardProps) => {
  const { sm: smDown } = useCssMediaQuery("down");
  const { languages } = useTeamConfig();

  return (
    <Paper key={uuid()} variant="outlined">
      <Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          padding="16px"
        >
          <Typography
            variant="body2"
            fontWeight={700}
            fontSize={smDown ? "0.8rem" : "0.9rem"}
            dangerouslySetInnerHTML={{ __html: leftTitle }}
          />
          <Typography
            variant="body2"
            fontWeight={700}
            fontSize={smDown ? "0.8rem" : "0.9rem"}
          >
            {rightTitle}
          </Typography>
        </Stack>
        <Divider />
        <Stack padding={"1rem"}>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={"0.5rem"}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              fontSize={"0.9rem"}
              fontWeight={"500"}
            >
              {languages?.purchaseSeats?.inventoryCardPricePerSeatLabel || "Price per seat"}:
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              fontSize={"0.9rem"}
              fontWeight={"500"}
            >
              {getUSCurrency(Number(price))}
            </Typography>
          </Box>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={"0.5rem"}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              fontSize={"0.9rem"}
              fontWeight={"500"}
            >
              Total Price:
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              fontSize={"0.9rem"}
              fontWeight={"500"}
            >
              {getUSCurrency(Number(price) * seatCount)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

/*
+------------------------------------------------------------------------------+
+
+
+
+
+ 
+
* [ SUITE-LEVEL Inventory Card]
+------------------------------------------------------------------------------+ */
interface SuiteLevelInventoryCardProps {
  inventory: InventoryType;
}

export function SuiteLevelInventoryCard(props: SuiteLevelInventoryCardProps) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const { purchaseSeatConfigs } = useTeamConfig();
  const { isCheckoutEditSeatsEnabled } = purchaseSeatConfigs;
  const inventory = props.inventory as SuitLevelInventory;
  const ticketEvent = useAppStore(
    selectEventsSelectedById(inventory.eventCode)
  );

  return (
    <Box
      sx={{
        outline: `1px solid ${theme.palette.divider}`,
        borderRadius: "6px",
      }}
    >
      <Box p={2} py={2.2}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {inventory?.suiteName
                ? `${inventory.suiteName}`
                : `Section ${inventory.sectionName}`}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Divider />

      <Stack padding={"1rem"}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"0.5rem"}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            Seat Count:
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            {inventory.seatCount}
          </Typography>
        </Box>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"0.5rem"}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            Total Price:
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            {getUSCurrency(Number(inventory.totalPrice))}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/*
+------------------------------------------------------------------------------+
+
+
+
+
+ 
+
* [ SUITE-LEVEL-V2 Inventory Card]
+------------------------------------------------------------------------------+ */
interface SuiteLevelInventoryCardProps {
  inventory: InventoryType;
}

export function SuiteLevelV2InventoryCard(props: SuiteLevelInventoryCardProps) {
  const theme = useTheme();
  const inventory = props.inventory as SuitLevelV2Inventory;
  const inventoryTotalPrice = calculateTicketPlusPerksPrice(inventory);
  const teamConfig = useTeamConfig();
  const { inventoryCardSettings } = teamConfig.uiConfigs;

  return (
    <Box
      sx={{
        outline: `1px solid ${theme.palette.divider}`,
        borderRadius: "6px",
      }}
    >
      <Box p={2} py={2.2}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {inventory?.suiteName
                ? `${calculateSuiteName(inventory?.suiteName, teamConfig)}`
                : `Section ${inventory.sectionName}`}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Divider />

      <Stack padding={"1rem"}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"0.5rem"}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            {inventoryCardSettings?.checkoutInventoryCard?.seatCountLabel ||
              "Seat Count: "}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            {inventory?.displayStandardSeatCount}
          </Typography>
        </Box>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"0.5rem"}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            {inventoryCardSettings?.checkoutInventoryCard?.totalPriceLabel ||
              "Total Price:"}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"500"}
          >
            {getUSCurrency(Number(inventoryTotalPrice))}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/*
+------------------------------------------------------------------------------+
+
+
+
+
+ 
+
* [ Group Level Inventory Card]
+------------------------------------------------------------------------------+ */
interface GroupLevelInventoryCardProps {
  inventory: GroupLevelInventory;
}

export function GroupLevelInventoryCard(props: GroupLevelInventoryCardProps) {
  const theme = useTheme();
  const inventory = props.inventory as GroupLevelInventory;
  const inventoryTotalPrice = calculateTicketPlusPerksPrice(inventory);
  const combinedSeats = createCombinedSeats(inventory.seats);

  return (
    <Box
      sx={{
        outline: `1px solid ${theme.palette.divider}`,
        borderRadius: "6px",
      }}
    >
      <Box p={2} py={2.2}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {inventory?.displaySectionName
                ? `Section ${inventory.displaySectionName}`
                : `Section ${inventory.sectionName}`}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body1"
              color="text.primary"
              fontSize={"0.9rem"}
              fontWeight={"600"}
            >
              {getUSCurrency(Number(inventoryTotalPrice))}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Divider />

      <Stack
        padding={2}
        direction={"row"}
        flexWrap={"wrap"}
        rowGap={1}
        columnGap={1}
      >
        {combinedSeats?.map((seatInfo, idx) => {
          return (
            <Stack
              key={idx}
              width={"100%"}
              p={1}
              px={2}
              border={`${theme.palette.divider} 1px solid`}
              borderRadius={1}
              direction={"row"}
              justifyContent={"center"}
              bgcolor={theme.palette.grey["100"]}
            >
              <Typography
                variant="body1"
                color="text.primary"
                fontSize={"0.9rem"}
                fontWeight={"500"}
              >
                Row {seatInfo.rowName},{" "}
                {seatInfo.firstSeat && !seatInfo.lastSeat
                  ? `Seat ${seatInfo.firstSeat}`
                  : null}
                {seatInfo.firstSeat && seatInfo.lastSeat
                  ? `Seat ${seatInfo.firstSeat} to ${seatInfo.lastSeat}`
                  : null}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      <Divider />

      <Stack padding={"1rem"} px={3}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"0.5rem"}
        >
          <Typography
            variant="body1"
            color="text.primary"
            fontSize={"0.9rem"}
            fontWeight={"600"}
          >
            Seat Count:
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            fontSize={"0.9rem"}
            fontWeight={"600"}
          >
            {inventory?.seatCount}
          </Typography>
        </Box>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"0.5rem"}
        >
          <Typography
            variant="body1"
            color="text.primary"
            fontSize={"0.9rem"}
            fontWeight={"600"}
          >
            Total Price:
          </Typography>
          <Typography
            variant="body1"
            color="text.primary"
            fontSize={"0.9rem"}
            fontWeight={"600"}
          >
            {getUSCurrency(Number(inventoryTotalPrice))}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/*
+------------------------------------------------------------------------------+




 

* [ SEAT-LEVEL-MULTI Inventory Card]
+------------------------------------------------------------------------------+ */
interface SeatLevelMultiInventoryCardProps {
  inventory: SeatLevelMultiInventory;
}

export function SeatLevelMultiInventoryCard(props: SeatLevelMultiInventoryCardProps) {
  const theme = useTheme();
  const inventory = props.inventory as SeatLevelMultiInventory;
  const teamConfig = useTeamConfig();
  const { sm: smDown } = useCssMediaQuery("down");

  // Use pricingInformation from inventory API response
  const pricingInfo = inventory.pricingInformation || [];
  
  // Calculate individual prices for each seat type from API data
  const calculateCategorizedPricing = () => {
    let totalPrice = 0;
    const breakdown: { title: string; count: number; pricePerSeat: number; total: number }[] = [];
    
    // Filter out zero seat counts and sort by orderIndex
    const validPricing = pricingInfo
      .filter((item: any) => item.seatCount > 0)
      .sort((a: any, b: any) => a.orderIndex - b.orderIndex);
    
    validPricing.forEach((pricingItem: any) => {
      const pricePerSeat = Number(pricingItem.allInPrice || pricingItem.basePrice);
      const total = pricePerSeat * pricingItem.seatCount;
      totalPrice += total;
      
      breakdown.push({
        title: pricingItem.pricingLabel.replace(':', ''), // Remove colon from "Adult Price:"
        count: pricingItem.seatCount,
        pricePerSeat,
        total,
      });
    });
    
    return { totalPrice, breakdown };
  };

  const { totalPrice, breakdown } = calculateCategorizedPricing();
  const totalSelectedSeats = pricingInfo.reduce((total: number, pricing: any) => total + pricing.seatCount, 0);

  return (
    <Box
      sx={{
        outline: `1px solid ${theme.palette.divider}`,
        borderRadius: "6px",
      }}
    >
      <Box p={2} py={2.2}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box>
            <Typography 
              variant="body2" 
              fontWeight={700} 
              color="text.primary"
              dangerouslySetInnerHTML={{ __html: createSeatLevelCardTitle(inventory, teamConfig) }}
            />
          </Box>
          <Box>
            <Typography
              variant="body1"
              color="text.primary"
              fontSize={smDown ? "0.8rem" : "0.9rem"}
              fontWeight={"600"}
            >
              {getUSCurrency(totalPrice)}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Divider />

      <Stack padding={"1rem"}>
        {/* Categorized pricing breakdown */}
        {breakdown.map((item, index) => (
          <Box
            key={index}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={"0.5rem"}
            mb={0.5}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              fontSize={smDown ? "0.8rem" : "0.9rem"}
              fontWeight={"500"}
            >
              {item.title} ({item.count}x {getUSCurrency(item.pricePerSeat)}):
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              fontSize={smDown ? "0.8rem" : "0.9rem"}
              fontWeight={"500"}
            >
              {getUSCurrency(item.total)}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 1 }} />

        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"0.5rem"}
        >
          <Typography
            variant="body1"
            color="text.primary"
            fontSize={smDown ? "0.8rem" : "0.9rem"}
            fontWeight={"600"}
          >
            Total ({totalSelectedSeats} seats):
          </Typography>
          <Typography
            variant="body1"
            color="text.primary"
            fontSize={smDown ? "0.8rem" : "0.9rem"}
            fontWeight={"600"}
          >
            {getUSCurrency(totalPrice)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function useStyles(theme: Theme) {
  return {
    inventoryTagWrapper: {
      position: "absolute",
      top: 0,
      right: "8px",
      zIndex: 1,
      transform: "translate(0%, -50%) scale(1)",
      overflow: "hidden",
    },
  };
}
